import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { startsWith, uniq } from 'lodash';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { AllFloorNumberingSchemesRequested, AllFoundationTypesRequested, AllGradesRequested, AllHandoverStandardsRequested, AllParkingTypesRequested, AllStateRepairsRequested, FloorNumberingScheme, FoundationType, Grade, HandoverStandard, ParkingTypeModel, selectAllFloorNumberingSchemes, selectAllFoundationTypes, selectAllGrades, selectAllHandoverStandards, selectAllParkingTypes, selectAllStateRepairs, StateRepair } from 'src/app/core/linked-tables';
import { AllSubCategorysRequested } from 'src/app/core/linked-tables/_actions/sub-category.actions';
import { AllSubTypeCategorysRequested } from 'src/app/core/linked-tables/_actions/sub-type-category.actions';
import { SubCategoryModel } from 'src/app/core/linked-tables/_models/sub-category.model';
import { SubTypeCategoryModel } from 'src/app/core/linked-tables/_models/sub-type-category.model';
import { selectAllSubCategorys } from 'src/app/core/linked-tables/_selectors/sub-category.selectors';
import { selectAllSubTypeCategorys } from 'src/app/core/linked-tables/_selectors/sub-type-category.selectors';
import { AppState } from 'src/app/core/reducers';
import { AccommodationLayoutTableComponent } from '../accommodation-layout-table/accommodation-layout-table.component';
import { Rooms } from '../size-module/mongolia-standard-table/mongolia-standard-table.types';
import { updateFormState } from 'src/app/core/v2/forms/form-state';
import { SpecialPurposePropertyTypeService } from 'src/app/core/v3/comparable/special-purpose/property-types';
import { formControlValueChanges } from 'src/app/core/v2/utils';
import { OccupancyStatusService } from 'src/app/core/v3/target-property/occupancy-status.service';
import { HotelAssetClassService, HotelAssetClassTypeID, HotelTypes, RoomSchedule } from 'src/app/core/v3/asset-class/hotel';
import { FileUploadService } from 'src/app/core/file-upload';
import { HttpEventType } from '@angular/common/http';
import { normalizeName } from 'src/app/core/v3/utils';

interface UploadFile {
  isUploaded: boolean,
  file: {
    name: string | null,
    display_name: string | null,
    path: string | null
  } | null
}

@Component({
  selector: 'kt-about-property',
  templateUrl: './about-property.component.html',
  styleUrls: ['./about-property.component.scss']
})
export class AboutPropertyComponent implements OnInit, OnDestroy {
  @ViewChild('hotelAccommodationFileInput', { static: false })
  hotelAccommodationFileInputRef!: ElementRef<HTMLInputElement>

  @ViewChild('roomScheduleFileInput', { static: false })
  roomScheduleFileInputRef!: ElementRef<HTMLInputElement>

  @ViewChild('outletsFileInput')
  outletsFileInputRef!: ElementRef<HTMLInputElement>

  private _unitOfAreaMeasurementId$ = new BehaviorSubject<number>(null)
  @Input()
  set unitOfAreaMeasurement(value: number) {
    this._unitOfAreaMeasurementId$.next(value)
  }
  unitOfAreaMeasurement$ = this._unitOfAreaMeasurementId$.asObservable().pipe(
    map(value => {
      if (value === null) {
        return ""
      }
      if (value === 1) {
        return " (sqm)"
      }
      return " (sqft)"
    })
  )

  @Input() propertySubTypeID: number;
  @Input() inputData: {
    hotel_type_id: number | null;
    hotel_rating_id: number | null;
    special_purpose_type_id: number;
    sub_type_category_id: number;
    sub_category_id: number;
    property_grade_id: number;
    general_desc: string;
    completion_year: string;
    apprx_year_extended: string;
    state_of_repair_id: number;
    handover_standard_id: number;
    foundation_type_id: number;
    above_floors: number;
    below_floors: number;
    construction_desc: string;
    floor_details: string;
    num_of_units: number;
    parking_type_id: number;
    automation: number;
    automation_type: string;
    floor_numbering_scheme_id: number;
    developer: string,
    anchor_tenant: string,
    has_ancillary_buildings: boolean;
    ancillary_buildings_num: number,
    property: string,
    completion_status: string,
    occupancy_status_id: number,
    usufruct_property: boolean,
    purpose_of_ownership_id: number,
    upgrades_condition_id: number,
    state_of_repair_description: string,
    views: string,
    layout_option: string,
    upgrades_conditions: { id: number, value: string }[],
    layout_descriptions: { id: number, title: string, description: string }[],

    estimated_age: number,
    estimated_remaining_lifespan_from: number,
    estimated_remaining_lifespan_to: number,
    makani_no: string,
    dewa_no: string,
    municipality_number: string,
    height: string,
    constructionCategories: { id: number, title: string, description: string }[],
    plot_number: string,
    plot_area: number,
    room_schedule_type: string,
    room_number_of_units: string | null,
    room_schedules: {id: number | null, room_type: string | null, number: number | null}[],
    hotel_accommodation_file: {name: string | null, path: string | null} | null,
    room_schedule_file: {name: string | null, path: string | null} | null,
    outlet_file: {name: string | null, path: string | null} | null
  }
  @Input() accommodation: {
    id: number;
    data: any;
    others: any;
  }
  @Input() isSavedBefore: boolean = false
  @Output() floorChanged = new EventEmitter<{ floors: number[], scheme_id: number }>();
  @Output() subTypeCategoryChanged = new EventEmitter<number>();
  @Output() roomChange = new EventEmitter<Rooms>();
  @Output() formStateChange = new EventEmitter<boolean | undefined>()
  @Output() buildingNumberChange = new EventEmitter<number>()

  @ViewChild(AccommodationLayoutTableComponent, { static: false })
  accommodationLayoutTableComponent: AccommodationLayoutTableComponent;

  form: UntypedFormGroup

  grades: Grade[] = [];
  parkingTypes: ParkingTypeModel[] = [];
  subTypeCategories: SubTypeCategoryModel[] = [];
  subCategories: SubCategoryModel[] = [];
  filteredSubCategories: SubCategoryModel[] = [];
  stateOfRepairs: StateRepair[] = [];
  handoverStandards: HandoverStandard[] = [];
  foundationTypes: FoundationType[] = [];
  floorNumberingSchemes: FloorNumberingScheme[] = [];
  aboveFloor$: BehaviorSubject<number> = new BehaviorSubject(0);
  belowFloor$: BehaviorSubject<number> = new BehaviorSubject(0);

  private _hotelAccommodationFile$ = new BehaviorSubject<UploadFile>({ isUploaded: false, file: null })
  hotelAccommodationFile$ = this._hotelAccommodationFile$.asObservable()
  private _roomScheduleFile$ = new BehaviorSubject<UploadFile>({ isUploaded: false, file: null })
  roomScheduleFile$ = this._roomScheduleFile$.asObservable()
  private _outletsFile$ = new BehaviorSubject<UploadFile>({ isUploaded: false, file: null })
  outletsFile$ = this._outletsFile$.asObservable()

  private subscriptions: Subscription[] = [];
  private _onDestroy: Subject<void> = new Subject();
  private _floors$: BehaviorSubject<number[]> = new BehaviorSubject([]);
  public floors$: Observable<number[]> = this._floors$.asObservable();

  fieldVisibility = {
    hotel_type_id: false,
    hotel_rating_id: false,
    sub_type_category_id: true,
    sub_category_id: true,
    special_purpose_type_id: false,
    property_grade_id: true,
    completion_year: true,
    apprx_year_extended: true,
    foundation_type_id: true,
    above_floors: true,
    below_floors: true,
    floor_details: false,
    construction_desc: true,
    number_of_units: false,
    parking_type_id: false,
    automation: false,
    developer: false,
    anchor_tenant: false,
    ancillary_buildings: false,
    property: false,

    estimated_age: false,
    estimated_remaining_lifespan: false,
    makani_no: false,
    municipality_number: false,
    dewa_no: false,
    building_name: false,
    plot_number: false,
    plot_area: false,
    floor_numbering: true,
    state_of_repair_id: true
  }
  errorFields = [];
  no0Floor: boolean = false;
  purposeTypes$ = this._purposeTypesService.types$
  occupancyStatusOptions$ = this._occupancyStatusService.options$
  purposesOfOwnership = [
    { id: 1, name: 'Investment' },
    { id: 2, name: 'Prime residence' },
    { id: 3, name: 'Secondary residence' }
  ]
  hotelTypes$ = new BehaviorSubject<{ id: number, name: string }[]>([])
  hotelRatings$ = new BehaviorSubject<{ id: number, name: string }[]>([])

  private _upgradesConditionsFormDataMap: number[] = []
  get upgradesConditionFormArray() {
    const formArray = this.form.get('upgrades_conditions') as UntypedFormArray
    return formArray
  }
  private _layoutDescriptionsFormDataMap: number[] = []
  get layoutDescriptionsFormArray() {
    const formArray = this.form.get('layout_descriptions') as UntypedFormArray
    return formArray
  }

  private _constructionCategoryIds: number[] = []
  constructionCategoryFormArray = new FormArray<FormGroup<{
    title: FormControl<string>,
    description: FormControl<string>
  }>>([])

  private _totalRoomSchedule$ = new BehaviorSubject<number>(0)
  totalRoomSchedule$ = this._totalRoomSchedule$.asObservable()
  private _roomScheduleIds: (number | null)[] = []
  roomScheduleFormArray = new FormArray<FormGroup<{
    room_type: FormControl<string | null>,
    number: FormControl<number | null>
  }>>([])

  constructor(
    private formBuilder: UntypedFormBuilder,
    private store: Store<AppState>,
    private _purposeTypesService: SpecialPurposePropertyTypeService,
    private _occupancyStatusService: OccupancyStatusService,
    private _hotelPropertyService: HotelAssetClassService,
    private _fileUploadService: FileUploadService
  ) { }

  ngOnInit(): void {
    this.no0Floor = this.inputData.floor_numbering_scheme_id == 2;
    switch (this.propertySubTypeID) {
      case 1:
        this.fieldVisibility = {
          ...this.fieldVisibility,
          property_grade_id: false,
          completion_year: false,
          apprx_year_extended: false,
          above_floors: false,
          foundation_type_id: false,
          below_floors: false,
          floor_details: true,
          construction_desc: false,
          number_of_units: false,
          parking_type_id: false,
          automation: false,
          property: true,
          plot_number: true,
          plot_area: true
        }
        this.floorChanged.emit({ floors: this._getFloorsFrom(this.inputData.floor_details), scheme_id: this.inputData.floor_numbering_scheme_id });
        this._floors$.next(this._getFloorsFrom(this.inputData.floor_details))
        break;
      case 3:
        this.fieldVisibility = {
          ...this.fieldVisibility,
          property_grade_id: false,
          completion_year: false,
          apprx_year_extended: false,
          above_floors: false,
          foundation_type_id: false,
          below_floors: false,
          floor_details: true,
          construction_desc: false,
          number_of_units: false,
          parking_type_id: false,
          automation: false
        }
        this.floorChanged.emit({ floors: this._getFloorsFrom(this.inputData.floor_details), scheme_id: this.inputData.floor_numbering_scheme_id });
        this._floors$.next(this._getFloorsFrom(this.inputData.floor_details))
        break;
      case 5:
        this.fieldVisibility = {
          ...this.fieldVisibility,
          property_grade_id: false,
          completion_year: false,
          apprx_year_extended: false,
          foundation_type_id: false,
          above_floors: false,
          below_floors: false,
          floor_details: true,
          construction_desc: false,
        }
        this.floorChanged.emit({ floors: this._getFloorsFrom(this.inputData.floor_details), scheme_id: this.inputData.floor_numbering_scheme_id });
        this._floors$.next(this._getFloorsFrom(this.inputData.floor_details))
        break;
      case 2:
        this.fieldVisibility = {
          ...this.fieldVisibility,
          property_grade_id: false,
          completion_year: false,
          apprx_year_extended: false,
          foundation_type_id: false,
          above_floors: false,
          below_floors: false,
          floor_details: true,
          construction_desc: false,
          number_of_units: true,
          parking_type_id: true,
          automation: true
        }
        this.floorChanged.emit({ floors: this._getFloorsFrom(this.inputData.floor_details), scheme_id: this.inputData.floor_numbering_scheme_id });
        this._floors$.next(this._getFloorsFrom(this.inputData.floor_details))
        break;
      case 18:
        this.fieldVisibility = {
          ...this.fieldVisibility,
          special_purpose_type_id: true,
          sub_type_category_id: false,
          sub_category_id: false,
          apprx_year_extended: false,
          developer: true,
          anchor_tenant: true,
          ancillary_buildings: true
        }
      case 7:
        this.fieldVisibility = {
          ...this.fieldVisibility,
          property: true,
          estimated_age: true,
          estimated_remaining_lifespan: true,
          makani_no: true,
          dewa_no: true,
          municipality_number: true,
          developer: true,
          building_name: true
        }
        break;
      case 11:
        this.fieldVisibility = {
          ...this.fieldVisibility,
          property: true,
          estimated_age: true,
          estimated_remaining_lifespan: true,
          makani_no: true,
          dewa_no: true,
          municipality_number: true,
          developer: true,
          building_name: true
        }
        break;
      case 17:
        this.fieldVisibility = {
          ...this.fieldVisibility,
          property: true,
          estimated_age: true,
          estimated_remaining_lifespan: true,
          makani_no: true,
          dewa_no: true,
          municipality_number: true,
          developer: true,
          building_name: true,
          plot_number: true,
          plot_area: true
        }
        break;
      case HotelAssetClassTypeID:
        this.fieldVisibility = {
          ...this.fieldVisibility,
          hotel_type_id: true,
          hotel_rating_id: true,
          sub_category_id: false,
          sub_type_category_id: false,
          estimated_age: true,
          estimated_remaining_lifespan: true,
          makani_no: true,
          dewa_no: true,
          municipality_number: true,
          developer: true,
          building_name: true,
          plot_area: true,
          plot_number: true,
          property_grade_id: false,
          apprx_year_extended: false,
          floor_numbering: false,
          state_of_repair_id: false,
          foundation_type_id: false,
          below_floors: false
        }
        break;
      default:
        this._floors$.next(this._getFloorsFromAboveBelowFromAc());
        break;
    }
    this.form = this.formBuilder.group({
      hotel_type_id: [this.inputData.hotel_type_id, this.fieldVisibility.hotel_type_id ? Validators.required : null],
      hotel_rating_id: [this.inputData.hotel_rating_id, this.fieldVisibility.hotel_rating_id ? Validators.required : null],
      special_purpose_type_id: [this.inputData.special_purpose_type_id, this.fieldVisibility.special_purpose_type_id ? Validators.required : null],
      sub_type_category_id: [this.inputData.sub_type_category_id, this.fieldVisibility.sub_type_category_id ? Validators.required : null],
      sub_category_id: [this.inputData.sub_category_id, this.fieldVisibility.sub_category_id ? Validators.required : null],
      property_grade_id: [this.inputData.property_grade_id, this.fieldVisibility.property_grade_id ? Validators.required : null],
      general_desc: [this.inputData.general_desc, this.propertySubTypeID == 1 ? null : Validators.required],
      completion_year: [this.inputData.completion_year],
      apprx_year_extended: [this.inputData.apprx_year_extended],
      state_of_repair_id: [this.inputData.state_of_repair_id],
      handover_standard_id: [this.inputData.handover_standard_id],
      foundation_type_id: [this.inputData.foundation_type_id],
      above_floors: [this.inputData.above_floors],
      below_floors: [this.inputData.below_floors, this.fieldVisibility.below_floors ? Validators.required : null],
      construction_desc: [this.inputData.construction_desc],
      floor_details: [this.inputData.floor_details && this.inputData.floor_details.length > 0
        ? this.inputData.floor_details.split(',')
        : '', this.fieldVisibility.floor_details ? Validators.required : null],
      number_of_units: [this.inputData.num_of_units, this.fieldVisibility.number_of_units ? Validators.required : null],
      parking_type_id: [this.inputData.parking_type_id, this.fieldVisibility.parking_type_id ? Validators.required : null],
      automation: [this.inputData.automation, this.fieldVisibility.automation ? Validators.required : null],
      automation_type: [this.inputData.automation_type],
      floor_numbering_scheme_id: [this.inputData.floor_numbering_scheme_id],
      developer: [this.inputData.developer],
      anchor_tenant: [this.inputData.anchor_tenant],
      has_ancillary_buildings: [this.inputData.has_ancillary_buildings],
      ancillary_buildings_num: [this.inputData.ancillary_buildings_num],
      property: [this.inputData.property],
      completion_status: [this.inputData.completion_status, Validators.required],
      occupancy_status_id: [this.inputData.occupancy_status_id, Validators.required],
      usufruct_property: [this.inputData.usufruct_property],
      purpose_of_ownership_id: [this.inputData.purpose_of_ownership_id],
      upgrades_condition_id: [this.inputData.upgrades_condition_id, Validators.required],
      state_of_repair_description: [this.inputData.state_of_repair_description, Validators.required],
      views: [this.inputData.views, Validators.required],
      layout_option: ['description'],
      upgrades_conditions: this._upgradesConditionFieldsFormArray(this.inputData.upgrades_conditions),
      layout_descriptions: this._layoutDescriptionFormArray(this.inputData.layout_descriptions),

      estimated_age: [this.inputData.estimated_age],
      estimated_remaining_lifespan_from: [this.inputData.estimated_remaining_lifespan_from],
      estimated_remaining_lifespan_to: [this.inputData.estimated_remaining_lifespan_to],
      makani_no: [this.inputData.makani_no],
      dewa_no: [this.inputData.dewa_no],
      municipality_number: [this.inputData.municipality_number],
      height: [this.inputData.height],
      plot_number: [this.inputData.plot_number, this.fieldVisibility.plot_number ? Validators.required : null],
      plot_area: [this.inputData.plot_area, this.fieldVisibility.plot_area ? Validators.required : null],
      room_schedule_type: [this.inputData.room_schedule_type],
      room_number_of_units: [this.inputData.room_number_of_units]
    });

    this._setConstructionCategoryFormArray(this.inputData.constructionCategories || [])
    this._setRoomScheduleFormArray(this.inputData.room_schedules || [])
    if (this.inputData.hotel_accommodation_file) {
      this._hotelAccommodationFile$.next({
        isUploaded: true,
        file: {
          name: this.inputData.hotel_accommodation_file.name,
          path: this.inputData.hotel_accommodation_file.path,
          display_name: normalizeName(this.inputData.hotel_accommodation_file.name)
        }
      })
    }
    if (this.inputData.room_schedule_file) {
      this._roomScheduleFile$.next({
        isUploaded: true,
        file: {
          name: this.inputData.room_schedule_file.name,
          path: this.inputData.room_schedule_file.path,
          display_name: normalizeName(this.inputData.room_schedule_file.name)
        }
      })
    }
    if (this.inputData.outlet_file) {
      this._outletsFile$.next({
        isUploaded: true,
        file: {
          name: this.inputData.outlet_file.name,
          path: this.inputData.outlet_file.path,
          display_name: normalizeName(this.inputData.outlet_file.name)
        }
      })
    }
    // this.form.controls.above_floors.valueChanges.pipe(
    //   startWith(this.form.controls.above_floors.value),
    //   takeUntil(this._onDestroy)
    // ).subscribe(val => this.aboveFloor$.next(val));

    // this.form.controls.below_floors.valueChanges.pipe(
    //   startWith(this.form.controls.below_floors.value),
    //   takeUntil(this._onDestroy)
    // ).subscribe(val => this.belowFloor$.next(val));

    this.form.controls.sub_type_category_id.valueChanges.pipe(
      startWith(this.form.controls.sub_type_category_id.value),
      takeUntil(this._onDestroy)
    ).subscribe(val => {
      this.filteredSubCategories = this._filterSubCategories(val)
      if (this.propertySubTypeID == 2) {
        this.fieldVisibility.floor_details = val == 20;
      }


      if (this.filteredSubCategories.length == 0) {
        this.form.controls.sub_category_id.clearValidators();
      } else {
        this.form.controls.sub_category_id.setValidators([Validators.required]);
      }
      this.form.controls.sub_category_id.updateValueAndValidity();

      this.subTypeCategoryChanged.emit(val);
    })

    this.form.controls.floor_numbering_scheme_id.valueChanges
      .pipe(
        startWith(this.form.controls.floor_numbering_scheme_id.value),
        takeUntil(this._onDestroy)
      )
      .subscribe(val => {
        this.no0Floor = val == 2;
        if (val == 2) {
          _changeFloorsValuated(this.form.controls.floor_details.value, this.form.controls.floor_details);
          this.floorChanged.emit({
            floors: this._floors$.value.filter(f => {
              if (this.no0Floor && f == 0) {
                return false;
              }
              return true;
            }), scheme_id: val
          })
          this._floors$.next(this._floors$.value.filter(f => {
            if (this.no0Floor && f == 0) {
              return false;
            }
            return true;
          }));
        } else {
          if (!this.fieldVisibility['floor_details']) {
            this._floors$.next(this._getFloorsFromAboveBelow());
            this.floorChanged.emit({ floors: this._getFloorsFromAboveBelow(), scheme_id: val })
          } else {
            this.floorChanged.emit({ floors: this._floors$.value, scheme_id: val })
          }
        }
      })

    this.form.controls.floor_details.valueChanges
      .pipe(
        takeUntil(this._onDestroy)
      )
      .subscribe(val => {
        if (this.form.controls.floor_numbering_scheme_id.value == 2) {
          _changeFloorsValuated(val, this.form.controls.floor_details);
        }
      })

    formControlValueChanges(this.form.controls.has_ancillary_buildings as FormControl).pipe(
      takeUntil(this._onDestroy)
    ).subscribe(value => {
      if (value) {
        this.form.controls.ancillary_buildings_num.setValidators(Validators.required)
      } else {
        this.form.controls.ancillary_buildings_num.removeValidators(Validators.required)
      }
      this.form.controls.ancillary_buildings_num.updateValueAndValidity({ emitEvent: false })
    })
    this.form.controls.ancillary_buildings_num.valueChanges.pipe(
      takeUntil(this._onDestroy)
    ).subscribe(value => {
      this.buildingNumberChange.emit(Number(value))
    })
    formControlValueChanges(this.form.controls.layout_option as FormControl).pipe(
      takeUntil(this._onDestroy)
    ).subscribe(value => {
      this.layoutDescriptionsFormArray.controls.forEach(control => {
        const group = control as UntypedFormGroup
        if (value == 'description') {
          group.controls.title.setValidators(Validators.required)
          group.controls.description.setValidators(Validators.required)
        } else {
          group.controls.title.removeValidators(Validators.required)
          group.controls.description.removeValidators(Validators.required)
        }
        group.controls.title.updateValueAndValidity()
        group.controls.description.updateValueAndValidity()
      })
    })

    formControlValueChanges(this.form.controls.hotel_type_id as FormControl)
      .pipe(
        switchMap(value => {
          if (value === HotelTypes.HotelApartments) {
            return this._hotelPropertyService.fetchHotelApartmentRatings()
          } else {
            return this._hotelPropertyService.fetchHotelRatings()
          }
        }),
        takeUntil(this._onDestroy)
      ).subscribe(value => {
        this.hotelRatings$.next(value)
      })

    formControlValueChanges(this.form.controls.room_schedule_type as FormControl).pipe(
      takeUntil(this._onDestroy)
    ).subscribe(value => {
      if (this.propertySubTypeID == HotelAssetClassTypeID) {
        if (value === 'upload') {
          this.form.controls.room_number_of_units.setValidators(Validators.required)
        } else {
          this.form.controls.room_number_of_units.removeValidators(Validators.required)
        }
      } else {
        this.form.controls.room_number_of_units.removeValidators(Validators.required)
      }
      this.form.controls.room_number_of_units.updateValueAndValidity()
    })

    this.roomScheduleFormArray.valueChanges.pipe(
      startWith(this.roomScheduleFormArray.value),
      takeUntil(this._onDestroy)
    ).subscribe(values => {
      // Total
      const total = values.reduce(
        (acc, value) => {
          if (value.number) {
            return acc + value.number
          }
          return acc
        },
        0
      )
      this._totalRoomSchedule$.next(total)
    })

    this.store.dispatch(new AllGradesRequested());
    const gradeSubscription = this.store.pipe(
      select(selectAllGrades))
      .subscribe(res => {
        this.grades = [];
        if (res) {
          this.grades = res;
        }
      });
    this.subscriptions.push(gradeSubscription);

    this.store.dispatch(new AllSubTypeCategorysRequested());
    const subTypeSub = this.store.pipe(
      select(selectAllSubTypeCategorys)
    ).subscribe(res => {
      this.subTypeCategories = res ? res.filter(item => item.property_sub_type_id == this.propertySubTypeID) : [];
    });
    this.subscriptions.push(subTypeSub);

    this.store.dispatch(new AllSubCategorysRequested());
    const subCatSub = this.store.select(selectAllSubCategorys)
      .subscribe(res => {
        this.subCategories = res ? res : [];
        this.filteredSubCategories = this._filterSubCategories(this.inputData.sub_type_category_id);
      });
    this.subscriptions.push(subCatSub);

    this.store.dispatch(new AllStateRepairsRequested())
    const stateRepairSub = this.store.select(selectAllStateRepairs)
      .subscribe(res => {
        this.stateOfRepairs = res ? res : [];
      });
    this.subscriptions.push(stateRepairSub);

    this.store.dispatch(new AllHandoverStandardsRequested())
    const handoverSub = this.store.select(selectAllHandoverStandards)
      .subscribe(res => {
        this.handoverStandards = res ? res : []
      });
    this.subscriptions.push(handoverSub);

    this.store.dispatch(new AllFoundationTypesRequested())
    const foundationSub = this.store.select(selectAllFoundationTypes)
      .subscribe(res => {
        this.foundationTypes = res ? res : []
      });
    this.subscriptions.push(foundationSub);

    this.store.dispatch(new AllParkingTypesRequested())
    const parkingTypeSub = this.store.select(selectAllParkingTypes)
      .subscribe(res => {
        this.parkingTypes = res ? res : []
      });
    this.subscriptions.push(parkingTypeSub);

    this.store.dispatch(new AllFloorNumberingSchemesRequested())
    const floorNumberingSchemeSub = this.store.select(selectAllFloorNumberingSchemes)
      .subscribe(res => {
        this.floorNumberingSchemes = res ? res : []
      });
    this.subscriptions.push(floorNumberingSchemeSub);

    this._hotelPropertyService.fetchHotelTypes().pipe(
      takeUntil(this._onDestroy)
    ).subscribe(values => this.hotelTypes$.next(values))

    this._updateFormState()
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  private _filterSubCategories(stcId: number) {
    if (stcId == null) {
      return [];
    }
    return this.subCategories.filter(item => item.sub_type_category_id == stcId);
  }

  private _getFloorsFromAboveBelowFromAc(): number[] {
    const l = -1 * (this.inputData.below_floors ? this.inputData.below_floors : 0);
    const h = (this.inputData.above_floors ? this.inputData.above_floors : 0);
    const numbers: number[] = [];
    for (let i = l; i < 0; i++) {
      numbers.push(i);
    }
    for (let i = 0; i <= h; i++) {
      numbers.push(i);
    }
    return numbers.filter(n => {
      if (this.no0Floor && n == 0) {
        return false;
      }
      return true;
    });
  }

  private _getFloorsFromAboveBelow(): number[] {
    const l = -1 * (this.form.controls.below_floors.value ? this.form.controls.below_floors.value : 0);
    const h = this.form.controls.above_floors.value ? this.form.controls.above_floors.value : 0;
    const numbers: number[] = [];
    for (let i = l; i <= h; i++) {
      numbers.push(i);
    }
    return numbers.filter(n => {
      if (this.no0Floor && n == 0) {
        return false;
      }
      return true;
    });
  }

  onFloorInput(event: any, type: 'above' | 'below') {
    const val = event.target.value;
    let l = 0;
    let h = 0;
    switch (type) {
      case 'above': {
        l = -1 * (this.form.controls.below_floors.value ? this.form.controls.below_floors.value : 0);
        h = val;
        break;
      }
      case 'below': {
        l = -1 * val;
        h = this.form.controls.above_floors.value ? this.form.controls.above_floors.value : 0;
        break;
      }
      default: {
        return;
      }
    }
    const numbers: number[] = [];
    for (let i = l; i < 0; i++) {
      numbers.push(i);
    }
    for (let i = 0; i <= h; i++) {
      numbers.push(i);
    }

    const _numbers = numbers.filter(n => {
      if (this.no0Floor && n == 0) {
        return false;
      }
      return true;
    });
    this.floorChanged.emit({ floors: _numbers, scheme_id: this.form.controls.floor_numbering_scheme_id.value });
    this._floors$.next(_numbers);
  }

  checkValidation(isComplete: boolean): boolean {
    let errorFields = [];
    let isValid = true;
    const controls = this.form.controls;
    if (isComplete && this.form.invalid) {
      Object.keys(controls).forEach(cname => {
        if (controls[cname].invalid) {
          console.log(cname)
          if (cname == 'completion_year') {
            errorFields.push('completion_year_about');
          } else {
            errorFields.push(cname);
          }
        }
        controls[cname].markAsTouched();
      })
      isValid = false;
    }

    if (isComplete) {
      // Check Layout description
      this.layoutDescriptionsFormArray.controls.forEach((control) => {
        const group = control as UntypedFormGroup
        if (group.invalid) {
          Object.keys(group.controls).forEach(cname => {
            errorFields.push(cname)
            group.controls[cname].markAsTouched()
          })
          isValid = false
        }
      })

      // Check upgrades
      this.upgradesConditionFormArray.controls.forEach(control => {
        if (control.invalid) {
          errorFields.push('Upgrades')
          control.markAsTouched()
          isValid = false
        }
      })
    }
    this.errorFields = errorFields;
    return isValid;
  }

  getData(): {
    about: AboutPropertyComponent['inputData']
    accommodation: AboutPropertyComponent['accommodation']
  } {
    const controls = this.form.controls;
    const hotelAccommodationFile = this._hotelAccommodationFile$.getValue()
    const roomScheduleFile = this._roomScheduleFile$.getValue()
    const outletFile = this._outletsFile$.getValue()
    return {
      about: {
        special_purpose_type_id: controls.special_purpose_type_id.value,
        sub_type_category_id: controls.sub_type_category_id.value,
        sub_category_id: controls.sub_category_id.value,
        property_grade_id: controls.property_grade_id.value,
        general_desc: controls.general_desc.value,
        completion_year: controls.completion_year.value,
        apprx_year_extended: controls.apprx_year_extended.value,
        state_of_repair_id: controls.state_of_repair_id.value,
        handover_standard_id: controls.handover_standard_id.value,
        foundation_type_id: controls.foundation_type_id.value,
        above_floors: controls.above_floors.value,
        below_floors: controls.below_floors.value,
        construction_desc: controls.construction_desc.value,
        floor_details: controls.floor_details.value.length > 0
          ? controls.floor_details.value.join(',')
          : '',
        num_of_units: controls.number_of_units.value,
        parking_type_id: controls.parking_type_id.value,
        automation: controls.automation.value,
        automation_type: controls.automation_type.value,
        floor_numbering_scheme_id: controls.floor_numbering_scheme_id.value,
        developer: controls.developer.value,
        anchor_tenant: controls.anchor_tenant.value,
        has_ancillary_buildings: controls.has_ancillary_buildings.value,
        ancillary_buildings_num: controls.ancillary_buildings_num.value,
        property: controls.property.value,
        completion_status: controls.completion_status.value,
        occupancy_status_id: controls.occupancy_status_id.value,
        usufruct_property: controls.usufruct_property.value,
        purpose_of_ownership_id: controls.purpose_of_ownership_id.value,
        upgrades_condition_id: controls.upgrades_condition_id.value,
        state_of_repair_description: controls.state_of_repair_description.value,
        views: controls.views.value,
        layout_option: controls.layout_option.value,
        upgrades_conditions: this._getUpgradesConditionValue(),
        layout_descriptions: this._getLayoutDescriptionsValue(),
        estimated_age: controls.estimated_age.value,
        estimated_remaining_lifespan_from: controls.estimated_remaining_lifespan_from.value,
        estimated_remaining_lifespan_to: controls.estimated_remaining_lifespan_to.value,
        makani_no: controls.makani_no.value,
        dewa_no: controls.dewa_no.value,
        municipality_number: controls.municipality_number.value,
        height: controls.height.value,
        constructionCategories: this._getConstructionCategoryValue(),
        plot_area: controls.plot_area.value,
        plot_number: controls.plot_number.value,
        hotel_rating_id: controls.hotel_rating_id.value,
        hotel_type_id: controls.hotel_type_id.value,
        room_schedule_type: controls.room_schedule_type.value,
        room_number_of_units: controls.room_number_of_units.value,
        room_schedules: this._getRoomScheduleValues(),
        hotel_accommodation_file: hotelAccommodationFile.file
          ? { name: hotelAccommodationFile.file.name, path: hotelAccommodationFile.file.path }
          : null,
        room_schedule_file: roomScheduleFile.file
          ? { name: roomScheduleFile.file.name, path: roomScheduleFile.file.path }
          : null,
        outlet_file: outletFile.file
          ? { name: outletFile.file.name, path: outletFile.file.path }
          : null,
      },
      accommodation: this.accommodationLayoutTableComponent
        ? { ...this.accommodationLayoutTableComponent.getData(), id: this.accommodation.id }
        : { data: null, others: [], id: this.accommodation.id }
    }
  }

  floorValidators: ValidatorFn[] = [
    (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as string;
      if (value == "" || value == undefined || value == null) {
        return { error: 'Value must be provided' }
      }
      if (!isNaN(parseInt(value))) {
        return null
      }
      const splits = value.split(":")
      if (splits.length == 2) {
        if (!isNaN(parseInt(splits[0])) && !isNaN(parseInt(splits[1]))) {
          return null;
        }
      }
      return { error: 'Floor error' }
    }
  ]
  onFloorAdd(event) {
    const floorStr = event.value;
    let floors = this._getFloors(floorStr);
    if (this.form.controls.floor_numbering_scheme_id.value == 2) {
      floors = floors.filter(f => f != 0);
    }
    const _floors = this._floors$.value;
    floors.forEach(floor => {
      if (_floors.findIndex(f => f == floor) > -1) {
        return;
      }
      _floors.push(floor);
    });
    _floors.sort();
    this.floorChanged.emit({ floors: _floors, scheme_id: this.form.controls.floor_numbering_scheme_id.value });
    this._floors$.next(_floors);
  }
  onFloorRemove(event) {
    const existingFloors = this._getFloorsFromControl(this.form.controls.floor_details.value);
    const floors = this._getFloors(event);
    const _floors = this._floors$.value;
    floors.forEach(floor => {
      const index = _floors.findIndex(f => f == floor);
      if (index > -1) {
        const fIndex = existingFloors.findIndex(f => f == floor);
        if (fIndex == -1) {
          _floors.splice(index, 1);
        }
      }
    });
    _floors.sort();
    this.floorChanged.emit({ floors: _floors, scheme_id: this.form.controls.floor_numbering_scheme_id.value });
    this._floors$.next(_floors);
  }

  onAddUpgradesAndSpecialCondition() {
    const control = this.formBuilder.control(null, [Validators.required])
    this.upgradesConditionFormArray.push(control)
    this._upgradesConditionsFormDataMap.push(null)
  }
  onRemoveUpgradesAndSpecialCondition(index: number) {
    this.upgradesConditionFormArray.removeAt(index)
  }

  onAddLayoutDescription() {
    const group = this.formBuilder.group({
      title: this.formBuilder.control(null, [Validators.required]),
      description: this.formBuilder.control(null, [Validators.required])
    })
    this.layoutDescriptionsFormArray.push(group)
    this._layoutDescriptionsFormDataMap.push(null)
  }
  onRemoveLayoutDescription(index: number) {
    this.layoutDescriptionsFormArray.removeAt(index)
  }

  private _getFloors(raw: string): number[] {
    const splits = raw.split(':');
    const floors: number[] = []
    if (splits.length == 2) {
      if (splits[0] == "") {
        floors.push(parseInt(raw));
      } else {
        const lower = parseInt(splits[0]);
        const upper = parseInt(splits[1]);
        for (let i = lower; i <= upper; i++) {
          floors.push(i);
        }
      }
    } else {
      floors.push(parseInt(splits[0]));
    }
    return floors;
  }

  private _getFloorsFrom(data: string) {
    const floorRaws = data == null ? [] : data.split(',');
    let floors = [];
    floorRaws.forEach(raw => {
      const _floors = this._getFloors(raw);
      _floors.forEach(f => floors.push(f));
    });
    floors = uniq(floors);
    return floors;
  }

  private _getFloorsFromControl(data: string[]) {
    let floors = [];
    data.forEach(raw => {
      const _floors = this._getFloors(raw);
      _floors.forEach(f => floors.push(f));
    });
    floors = uniq(floors);
    return floors;
  }

  onRoomChanged(event: Rooms) {
    this.roomChange.emit(event);
  }

  private _updateFormState() {
    const result = updateFormState(
      this.form,
      ['sub_type_category_id', 'sub_category_id', 'property_grade_id', 'general_desc',
        'completion_year', 'state_of_repair_id', 'handover_standard_id',
        'above_floors', 'below_floors', 'floor_details', 'number_of_units',
        'automation', 'automation_type'
      ],
      this.isSavedBefore
    )

    this.formStateChange.emit(result)
  }

  private _upgradesConditionFieldsFormArray(items: { id: number, value: string }[]) {
    const formArray = this.formBuilder.array([])
    items.forEach(item => {
      const control = this.formBuilder.control(item.value, [Validators.required])
      formArray.push(control)
      this._upgradesConditionsFormDataMap.push(item.id)
    })
    return formArray
  }
  private _getUpgradesConditionValue() {
    let vals: { id: number, value: string }[] = []
    this.upgradesConditionFormArray.controls.forEach((control, i) => {
      const _new = { id: null, value: null }
      if (this._upgradesConditionsFormDataMap[i]) {
        _new.id = this._upgradesConditionsFormDataMap[i]
      }
      _new.value = control.value
      vals.push(_new)
    })
    return vals;
  }

  private _layoutDescriptionFormArray(items: { id: number, title: string, description: string }[]) {
    const formArray = this.formBuilder.array([])
    if (items.length === 0) {
      [{ id: null, title: null, description: null }].forEach(item => {
        const control = this.formBuilder.group({
          title: this.formBuilder.control(item.title, [Validators.required]),
          description: this.formBuilder.control(item.description, [Validators.required])
        })
        formArray.push(control)
        this._layoutDescriptionsFormDataMap.push(item.id)
      })
    } else {
      items.forEach(item => {
        const control = this.formBuilder.group({
          title: this.formBuilder.control(item.title, [Validators.required]),
          description: this.formBuilder.control(item.description, [Validators.required])
        })
        formArray.push(control)
        this._layoutDescriptionsFormDataMap.push(item.id)
      })
    }
    return formArray
  }
  private _getLayoutDescriptionsValue() {
    let vals: { id: number, title: string, description: string }[] = []
    this.layoutDescriptionsFormArray.controls.forEach((control, i) => {
      const _new = { id: null, title: null, description: null }
      const form = control as UntypedFormGroup
      if (this._layoutDescriptionsFormDataMap[i]) {
        _new.id = this._layoutDescriptionsFormDataMap[i]
      }
      _new.title = form.controls.title.value
      _new.description = form.controls.description.value
      vals.push(_new)
    })
    return vals;
  }

  onAddConstructionCategory() {
    this.constructionCategoryFormArray.push(new FormGroup({
      title: new FormControl(null),
      description: new FormControl(null)
    }))
    this._constructionCategoryIds.push(null)
  }
  onRemoveConstructionCategory(index: number) {
    this.constructionCategoryFormArray.removeAt(index)
  }
  private _setConstructionCategoryFormArray(items: { id: number, title: string, description: string }[]) {
    if (items.length === 0) {
      this.constructionCategoryFormArray.push(new FormGroup({
        title: new FormControl(null),
        description: new FormControl(null)
      }))
      this._constructionCategoryIds.push(null)
    } else {
      items.forEach(item => {
        this.constructionCategoryFormArray.push(new FormGroup({
          title: new FormControl(item.title),
          description: new FormControl(item.description)
        }))
        this._constructionCategoryIds.push(item.id)
      })
    }
  }
  private _getConstructionCategoryValue() {
    const vals: { id: number, title: string, description: string }[] = []
    this.constructionCategoryFormArray.controls.forEach((group, i) => {
      const _new = { id: null, title: null, description: null }
      if (this._constructionCategoryIds[i]) {
        _new.id = this._constructionCategoryIds[i]
      }
      _new.title = group.controls.title.value
      _new.description = group.controls.description.value
      vals.push(_new)
    })
    return vals;
  }

  onClickUploadHotelAccommodation() {
    this.hotelAccommodationFileInputRef.nativeElement.click()
  }

  onUploadHotelAccommodationFile(event: any) {
    const files: FileList = event.target.files
    const formData = new FormData()
    Array.from(files).forEach((file, i) => {
      formData.append('files[' + i + ']', file, file.name)
    })
    event.srcElement.value = null

    this._fileUploadService.uploadFile(formData).subscribe(value => {
      if (value.type !== HttpEventType.Response) {
        return;
      }
      const body = value.body as any
      this._hotelAccommodationFile$.next({
        isUploaded: true,
        file: {
          name: body.uploaded_files[0].name,
          path: body.uploaded_files[0].path,
          display_name: normalizeName(body.uploaded_files[0].name)
        }
      })
    })
  }

  onClickUploadRoomSchedule() {
    this.roomScheduleFileInputRef.nativeElement.click()
  }

  onUploadRoomScheduleFile(event: any) {
    const files: FileList = event.target.files
    const formData = new FormData()
    Array.from(files).forEach((file, i) => {
      formData.append('files[' + i + ']', file, file.name)
    })
    event.srcElement.value = null

    this._fileUploadService.uploadFile(formData).subscribe(value => {
      if (value.type !== HttpEventType.Response) {
        return;
      }
      const body = value.body as any
      this._roomScheduleFile$.next({
        isUploaded: true,
        file: {
          name: body.uploaded_files[0].name,
          path: body.uploaded_files[0].path,
          display_name: normalizeName(body.uploaded_files[0].name)
        }
      })
    })
  }

  onAddRoomSchedule() {
    this._addRoomSchedule({ id: null, room_type: null, number: null })
  }

  onRemoveRoomSchedule(index: number) {
    this.roomScheduleFormArray.removeAt(index)
  }

  private _setRoomScheduleFormArray(items: RoomSchedule[]) {
    this.roomScheduleFormArray.clear()
    this._roomScheduleIds = []
    const _items = items.length === 0
      ? [{ id: null, room_type: null, number: null }]
      : [...items]
    _items.forEach(item => this._addRoomSchedule(item))
  }

  private _addRoomSchedule(item: RoomSchedule) {
    this.roomScheduleFormArray.push(new FormGroup({
      room_type: new FormControl<string | null>(item.room_type),
      number: new FormControl<number | null>(item.number)
    }))
    this._roomScheduleIds.push(item.id)
  }

  private _getRoomScheduleValues(): RoomSchedule[] {
    const values: RoomSchedule[] = []
    this.roomScheduleFormArray.controls.forEach((group, i) => {
      const _new: RoomSchedule = { id: null, number: null, room_type: null }
      if (this._roomScheduleIds[i]) {
        _new.id = this._roomScheduleIds[i]
      }
      _new.number = group.controls.number.value
      _new.room_type = group.controls.room_type.value
      values.push(_new)
    })
    return values
  }

  onClickUploadOutlets() {
    this.outletsFileInputRef.nativeElement.click()
  }

  onUploadOutletsFile(event: any) {
    const files: FileList = event.target.files
    const formData = new FormData()
    Array.from(files).forEach((file, i) => {
      formData.append('files[' + i + ']', file, file.name)
    })
    event.srcElement.value = null

    this._fileUploadService.uploadFile(formData).subscribe(value => {
      if (value.type !== HttpEventType.Response) {
        return;
      }
      const body = value.body as any
      this._outletsFile$.next({
        isUploaded: true,
        file: {
          name: body.uploaded_files[0].name,
          path: body.uploaded_files[0].path,
          display_name: normalizeName(body.uploaded_files[0].name)
        }
      })
    })
  }
}

function _changeFloorsValuated(val: any, control: AbstractControl) {
  if ((typeof val) != 'string') {
    const _val = val.map(v => {
      const splits = v.split(':');
      if (splits.length == 2 && splits[0] == '0') {
        return { value: `1-${splits[1]}`, update: true }
      } else if (splits.length == 1 && splits[0] == '0') {
        return { value: null, update: true }
      }
      return { value: v, update: false }
    })
    const shouldUpdate = _val.find(v => v.update);
    if (shouldUpdate) {
      control.setValue(_val.filter(v => v.value != null).map(v => v.value));
      control.updateValueAndValidity();
    }
  }
}